// import React, { Component } from "react";
import React, { useState, useEffect, Component, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { Dimensions, View, Text, ActivityIndicator } from 'react-native';
import { APP_COLOURS } from './APP_VARS';
import { ThemeProvider } from './ThemeContext';

// Error Boundary to catch chunk load errors and other runtime errors
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Check if it's a chunk load error
    if (error.name === 'ChunkLoadError' || 
        (error.message && error.message.includes('Loading chunk'))) {
      // Clear cache and reload
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
    }
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    // Clear any cached data
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    // Force reload from server
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            height: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: APP_COLOURS.BG,
            padding: 20,
          }}
        >
          <div
            style={{
              backgroundColor: APP_COLOURS.BG2,
              padding: 40,
              borderRadius: 12,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <h2 style={{ color: APP_COLOURS.WHITE, marginBottom: 16, fontFamily: 'Gotham' }}>
              Update Available
            </h2>
            <p style={{ color: APP_COLOURS.TEXTWHITE, marginBottom: 24, fontFamily: 'Gotham' }}>
              A new version of SKrum is available. Please reload to get the latest updates.
            </p>
            <button
              onClick={this.handleReload}
              style={{
                backgroundColor: APP_COLOURS.BLUE,
                color: APP_COLOURS.WHITE,
                border: 'none',
                padding: '12px 32px',
                borderRadius: 8,
                fontSize: 16,
                fontFamily: 'Gotham',
                cursor: 'pointer',
              }}
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Replace direct imports with lazy imports
const DevicePolicy = React.lazy(() => import('./OpenUrls/DevicePolicy'));
const PostApproval = React.lazy(() =>
  import('./Authed/ContentCalendar/PostApproval')
);
const PerformanceReview = React.lazy(() =>
  import('./Authed/PerformanceReview/PerformanceReview')
);
const Home = React.lazy(() => import('./Authed/Home'));
const Login = React.lazy(() => import('./Unauthed/Login'));
const HomeMobile = React.lazy(() => import('./Authed/HomeMobile'));
const CompanyDocuments = React.lazy(() =>
  import('./OpenUrls/CompanyDocuments')
);
const LiveStream = React.lazy(() => import('./OpenUrls/STREAM'));
const ContentApproval = React.lazy(() =>
  import('./Authed/ContentCalendar/ContentApproval')
);
const BatchApproval = React.lazy(() =>
  import('./Authed/ContentCalendar/BatchApproval')
);
// project router is used to view and edit projects from the project list with the project component
const ProjectRouter = React.lazy(() =>
  import('./Authed/DailyPlanner/ProjectRouter')
);
// task router is used to view and edit tasks from the task list with the task component and project router is used to view and edit projects from the project list with the project component url is /home/tasks/:task_id
// task router is used to view and edit tasks from the task list with the task component and project router is used to view and edit projects from the project list with the project component url is /home/projects/:project_id
const TaskRouter = React.lazy(() =>
  import('./Authed/DailyPlanner/TaskRouter')
);
// Standalone document editor for project descriptions - opens in new tab
const ProjectDocumentEditor = React.lazy(() =>
  import('./Authed/DailyPlanner/ProjectDocumentEditor')
);
// Standalone project report page - shareable presentation-style report
const ProjectReportPage = React.lazy(() =>
  import('./Authed/DailyPlanner/ProjectReportPage')
);
const AllPresentations = React.lazy(() =>
  import('./Authed/PresentationApp/AllPresentations')
);

const BrandRouter = React.lazy(() =>
  import('./Authed/BrandTracker/BrandRouter')
);

// Dedicated Chats Page - can be opened as standalone tab
const ChatsPage = React.lazy(() =>
  import('./Authed/Chats/ChatsPage')
);

// Dedicated TimeBox Page - full screen time management
const TimeBoxPage = React.lazy(() =>
  import('./Authed/Timebox/TimeBoxPage')
);

// Public shared document viewer (no auth required)
const SharedDocument = React.lazy(() =>
  import('./OpenUrls/SharedDocument')
);

// import { ProposalUrl } from "./OpenUrls/Proposals/ProposalUrl";
// import { PresentationUrl } from "./OpenUrls/Presentations";

if (typeof window !== 'undefined') {
  require('smoothscroll-polyfill').polyfill();
}

function useQuery() {
  // WORKS FOR QURY URL
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ResponsiveHome = () => {
  // State to hold screen width
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  let query = useQuery();
  const { page } = useParams(); // This hooks allows you to access the
  let project_id = query.get('pid');
  // Effect to listen for screen resize
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Conditionally rendering based on screen width
  const renderComponent =
    screenWidth < 700 ? (
      <HomeMobile page={page} project_id={project_id} />
    ) : (
      <Home page={page} project_id={project_id} />
    );

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      {renderComponent}
    </div>
  );
};

class index extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      console.log('Polly fill for smooth scroll in safari');
    } else {
      // production code
    }

    this.resize();
  }

  resize() {
    this.setState({
      screenHeight: window.innerHeight,
      screenWidth: window.innerWidth,
    });
  }

  render() {
    return (
      <ThemeProvider>
        <ChunkErrorBoundary>
          <Router>
          <Suspense
            fallback={
              <div
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: APP_COLOURS.BG,
                }}
              >
                {/* <img
								src='/skrum_menu.png'
								alt='Loading...'
								style={{
									width: 50,
									height: 50,
								}}
							/> */}
              </div>
            }
          >
            <Routes>
            <Route path='/login' element={<Login />} />
            {/* <Route
							path='/Home/:page/:project'
							element={
								this.state.screenWidth < 700 ? (
									<HomeMobile />
								) : (
									<Home />
								)
							}
						/> */}

            <Route
              path='/home/:page'
              element={
                this.state.screenWidth < 700 ? (
                  <HomeMobile />
                ) : (
                  <ResponsiveHome />
                )
              }
            />

            <Route
              path='/home/projects/:project_id'
              element={<ProjectRouter />}
            />

            <Route
              path='/home/projects/:project_id/document'
              element={<ProjectDocumentEditor />}
            />

            <Route
              path='/home/projects/:project_id/report'
              element={<ProjectReportPage />}
            />

            <Route path='/home/tasks/:task_id' element={<TaskRouter />} />

            <Route path='/home/presentations' element={<AllPresentations />} />

            {/* Dedicated Chats page - can be opened as a new tab */}
            <Route path='/home/chats' element={<ChatsPage />} />

            {/* Dedicated TimeBox page - full screen time management */}
            <Route path='/home/timebox-fullscreen' element={<TimeBoxPage />} />

            <Route path='/home/brandtracker/:id' element={<BrandRouter />} />

            {/* Direct brand tracker route (without /home prefix) */}
            <Route path='/brandtracker/:id' element={<BrandRouter />} />

            <Route
              path='/postapproval/:postid/:token'
              element={<PostApproval />}
            />

            <Route
              path='/batchapproval'
              element={<BatchApproval />}
            />

            <Route
              path='/caa/:contentid/:token'
              element={<ContentApproval />}
            />

            <Route
              path='/performancereview/:surveyId/:token'
              element={<PerformanceReview />}
            />

            <Route path='/livestream/:token' element={<LiveStream />} />

            {/* Public shared document view (no auth required) */}
            <Route path='/shared/document/:token' element={<SharedDocument />} />

            {/* 192.168.20.11:3000/postapproval/retinolhpr1/xV3-0Ea5T2eLbRJYo-eGoT0Dq_Lq9o1-kyGK5gg */}

            <Route
              path='/smreport/:brand/:token'
              element={<PerformanceReview />}
            />

            <Route
              path='/doc/:documentId/:token'
              element={<CompanyDocuments />}
            />

            {/* <Route
							path='/proposal/:proposal_id/:token'
							element={<ProposalUrl />}
						/>

						<Route
							path='/presentation/:presentation_id/:token'
							element={<PresentationUrl />}
						/> */}

            <Route path='*' element={<Navigate to='/login' />} />
            </Routes>
          </Suspense>
          </Router>
        </ChunkErrorBoundary>
      </ThemeProvider>
    );
  }
}

export default index;
